import { ShieldCheck, X } from 'lucide-react';
import React, { useState } from 'react';
import { changeAccountPassword } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';

type AccountSecurityDialogProps = {
  onClose: () => void;
};

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};

function AccountSecurityDialog({ onClose }: AccountSecurityDialogProps) {
  const { session } = useSession();
  const { toast } = useFeedback();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');

    if (!session) return;
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await changeAccountPassword(session, currentPassword, newPassword);
      toast('success', 'Password updated. Other sessions have been signed out.');
      onClose();
    } catch (cause: unknown) {
      const failure = cause as RequestError;
      setError(
        failure.response?.data?.message ||
          failure.message ||
          'Failed to change password.'
      );
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 10 &&
    confirmPassword.length > 0;
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form
        className="dialogPanel accountSecurityDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-security-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Personal account</div>
            <h3 id="account-security-title">Account security</h3>
            <p>
              Change the password for <strong>{session?.user.email}</strong>.
              Other signed-in sessions will be ended.
            </p>
          </div>
          <button
            type="button"
            className="iconButton"
            onClick={close}
            aria-label="Close account security"
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>

        <div className="accountSecurityIdentity">
          <span><ShieldCheck size={16} /></span>
          <div>
            <strong>{session?.user.displayName || 'Signed-in user'}</strong>
            <small>{session?.user.email}</small>
          </div>
        </div>

        <div className="settingsForm accountSecurityForm">
          <label className="settingsField">
            <span>Current password</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
          </label>
          <label className="settingsField">
            <span>New password</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={10}
              required
            />
          </label>
          <label className="settingsField">
            <span>Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <p className="fieldHint">
            At least 10 characters, with three of: lowercase, uppercase,
            numbers, symbols.
          </p>
          {error ? <p className="fieldError">{error}</p> : null}
        </div>

        <div className="dialogActions">
          <button
            type="button"
            className="secondaryButton"
            onClick={close}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="primaryButton"
            disabled={busy || !canSubmit}
          >
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { AccountSecurityDialog };
